import { z } from 'zod';
import { AttributeType, ValueDataType, type AttributeToken } from '@sleed/bizcommerce-api';

/**
 * Attribute edit form. Field names mirror the `AttributeToken` DTO (abbreviated server keys).
 * Validation mirrors the legacy admin grid (Core/client/admin/src/pages/Catalog/Attributes/index.js):
 * nm required ≤100, at/vt required, si required.
 *
 * Unlike AttributeCategory, every scalar field here lives on the base `Attribute` entity
 * (Core/.../Domain/Catalog/Attribute.cs), so Code/type/flags/etc. all round-trip in the default
 * data-entry language. Name/ValueFormatString/InfoText are also translated (ML), but the base
 * row carries the default-language value.
 *
 * Images (img1/img2) are intentionally not handled here yet — they require the two-phase
 * save-then-upload orchestration (binaryRepoImagesApi.uploadAttribute); flagged as a follow-up.
 */
export const attributeSchema = z.object({
	id: z.number().optional(),
	cd: z.string().trim().max(50, 'validation.tooLong').optional(),
	nm: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
	dnm: z.string().trim().max(250, 'validation.tooLong').optional(),
	acid: z.number().nullable().optional(),
	at: z.nativeEnum(AttributeType),
	vt: z.nativeEnum(ValueDataType),
	imd: z.boolean(),
	isrc: z.boolean(),
	iiv: z.boolean(),
	daf: z.boolean(),
	vfs: z.string().trim().max(50, 'validation.tooLong').optional(),
	itxt: z.string().optional(),
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int(),
	erpcd: z.string().trim().max(50, 'validation.tooLong').optional(),
	// Display-only existing image URLs (size "S"), carried from the token to seed the uploader previews.
	// Uploads/deletions are handled out-of-band (binaryRepoImagesApi + imgdel flags), not via these.
	img1: z.string().optional(),
	img2: z.string().optional()
});

export type AttributeFormValues = z.infer<typeof attributeSchema>;

export const EMPTY_ATTRIBUTE: AttributeFormValues = {
	cd: '',
	nm: '',
	dnm: '',
	acid: null,
	at: AttributeType.SingleValue,
	vt: ValueDataType.Text,
	imd: false,
	isrc: false,
	iiv: false,
	daf: false,
	vfs: '',
	itxt: '',
	iv: true,
	si: 0,
	erpcd: '',
	img1: '',
	img2: ''
};

export function tokenToForm(token: AttributeToken): AttributeFormValues {
	return {
		id: token.id,
		cd: token.cd ?? '',
		nm: token.nm ?? '',
		dnm: token.dnm ?? '',
		acid: token.acid ?? null,
		at: token.at ?? AttributeType.SingleValue,
		vt: token.vt ?? ValueDataType.Text,
		imd: token.imd ?? false,
		isrc: token.isrc ?? false,
		iiv: token.iiv ?? false,
		daf: token.daf ?? false,
		vfs: token.vfs ?? '',
		itxt: token.itxt ?? '',
		iv: token.iv ?? false,
		si: token.si ?? 0,
		erpcd: token.erpcd ?? '',
		img1: token.img1 ?? '',
		img2: token.img2 ?? ''
	};
}

export function formToToken(form: AttributeFormValues): AttributeToken {
	return {
		id: form.id,
		cd: form.cd?.trim() || null,
		nm: form.nm.trim(),
		dnm: form.dnm?.trim() || null,
		acid: form.acid ?? null,
		at: form.at,
		vt: form.vt,
		imd: form.imd,
		isrc: form.isrc,
		iiv: form.iiv,
		daf: form.daf,
		vfs: form.vfs?.trim() || null,
		itxt: form.itxt || null,
		iv: form.iv,
		si: form.si,
		erpcd: form.erpcd?.trim() || null
	};
}
